import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { isValidVariant } from "../lib/config/ab-tests";
import { setABTestVariant } from "../lib/utils/ab-test-cookies";

/**
 * Component to handle query parameters at the app root level
 * Extracts and saves access_token, refresh_token, referral code to localStorage
 * Handles A/B test variant parameter (v) and stores in cookie if valid
 */
export default function QueryParamHandler() {
	const [searchParams] = useSearchParams();

	useEffect(() => {
		if (typeof window === "undefined") return;

		let hasChanges = false;

		// Check for access token (either access_token or _token)
		const accessToken = searchParams.get("access_token") || searchParams.get("_token");
		if (accessToken) {
			const existingToken = localStorage.getItem("accessToken");
			if (existingToken !== accessToken) {
				localStorage.setItem("accessToken", accessToken);
				hasChanges = true;
			}
		}

		// Check for refresh token (either refresh_token or _refresh)
		const refreshToken = searchParams.get("refresh_token") || searchParams.get("_refresh");
		if (refreshToken) {
			const existingRefreshToken = localStorage.getItem("refreshToken");
			if (existingRefreshToken !== refreshToken) {
				localStorage.setItem("refreshToken", refreshToken);
				hasChanges = true;
			}
		}

		// Check for referral code (referred_by or referral_code)
		const referralCode = searchParams.get("referred_by") || searchParams.get("referral_code");
		if (referralCode) {
			const existingReferralCode = localStorage.getItem("referral_code");
			if (existingReferralCode !== referralCode) {
				localStorage.setItem("referral_code", referralCode);
				hasChanges = true;
			}
		}

		// Handle A/B test variant parameter (v)
		const variant = searchParams.get("v");
		if (variant) {
			// Only set cookie if variant is valid
			if (isValidVariant(variant)) {
				setABTestVariant(variant);
				// Dispatch event for variant change
				window.dispatchEvent(new CustomEvent("abTestVariantChanged", { detail: { variant } }));
			}
			// If invalid, do nothing - preserves existing cookie if present
		}

		// Dispatch event if tokens were stored/updated
		if (hasChanges && (accessToken || refreshToken)) {
			window.dispatchEvent(new Event("tokenStored"));
		}
	}, [searchParams]);

	return null;
}

